import React, { useEffect, useState } from 'react';
import api, { notifyUpdate } from '../../lib/api';import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const fmtDate = (d) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${day}.${m}.${y}.`; };
const empty = { title: '', date: new Date().toISOString().slice(0, 10), excerpt: '', content: '', author: 'Ministarstvo privrede' };

export default function AdminObavijesti() {
  const [notices, setNotices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const load = () => api.get('/notices').then((res) => setNotices(res.data)).catch(() => {});
    load();
    window.addEventListener('store-updated', load);
    return () => window.removeEventListener('store-updated', load);
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (n) => { setForm(n); setEditId(n.id); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.excerpt || !form.content) { toast.error('Popunite naslov, sažetak i sadržaj.'); return; }
    try {
      if (editId) {
        await api.put(`/notices/${editId}`, form);
        toast.success('Obavijest je ažurirana.');
      } else {
        await api.post('/notices', form);
        toast.success('Obavijest je objavljena.');
      }
      notifyUpdate();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri spremanju.');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/notices/${id}`);
      toast.success('Obavijest je obrisana.');
      notifyUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri brisanju.');
    }
  };

  return (
    <div data-testid="admin-obavijesti">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0b1a3d]">Obavijesti</h1>
          <p className="text-sm text-slate-500">Upravljanje obavijestima portala.</p>
        </div>
        <Button onClick={openNew} className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200" data-testid="add-notice-btn">
          <Plus className="h-4 w-4 mr-2" /> Nova obavijest
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#f8f9fd]">
              <TableHead className="text-[#001752] font-semibold">Naslov</TableHead>
              <TableHead className="text-[#001752] font-semibold w-32">Datum</TableHead>
              <TableHead className="text-[#001752] font-semibold w-44 hidden md:table-cell">Autor</TableHead>
              <TableHead className="w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notices.map((n) => (
              <TableRow key={n.id} data-testid={`notice-row-${n.id}`}>
                <TableCell className="font-medium text-slate-700 max-w-md truncate">{n.title}</TableCell>
                <TableCell className="text-slate-500">{fmtDate(n.date)}</TableCell>
                <TableCell className="text-slate-500 hidden md:table-cell">{n.author}</TableCell>
                <TableCell>
                  <div className="flex gap-1.5 justify-end">
                    <Button size="icon" variant="outline" onClick={() => openEdit(n)} data-testid={`edit-notice-${n.id}`}
                      className="h-8 w-8 border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white rounded-md transition-colors duration-200">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="outline" data-testid={`delete-notice-${n.id}`}
                          className="h-8 w-8 border-slate-200 text-red-600 hover:bg-red-600 hover:text-white rounded-md transition-colors duration-200">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Brisanje obavijesti</AlertDialogTitle>
                          <AlertDialogDescription>Da li ste sigurni da želite obrisati obavijest „{n.title}“?</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Odustani</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(n.id)} className="bg-red-600 hover:bg-red-700">Obriši</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="notice-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0b1a3d]">{editId ? 'Uredi obavijest' : 'Nova obavijest'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 mb-1.5 block">Naslov *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notice-form-title" className="border-slate-200" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600 mb-1.5 block">Datum objave *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="notice-form-date" className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Autor</Label>
                <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Sažetak *</Label>
              <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="notice-form-excerpt" className="border-slate-200" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Sadržaj *</Label>
              <Textarea rows={7} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="notice-form-content" className="border-slate-200" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-200">Odustani</Button>
            <Button onClick={save} className="bg-[#001752] hover:bg-[#0a2a7a] text-white transition-colors duration-200" data-testid="notice-form-save">Sačuvaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
